import NotificationsCenter from "./NotificationsCenter";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  UsersIcon, 
  UserGroupIcon, 
  TrophyIcon,
  ShieldCheckIcon,
  UserPlusIcon,
  LinkIcon,
  UserIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
// UI components and utilities
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { fetchAdminAnalytics, formatTrendData } from '../../utils/analytics';
import { asAbortSignal, withAbortSignal } from '../../utils/abortSignal';
import toast from 'react-hot-toast';
import { useNotifications } from '../../hooks/useNotifications';
import { usePendingGiftsMenuBadge } from '../../hooks/usePendingGiftsMenuBadge';
import DashboardLayout from '../ui/DashboardLayout';
import BackToTopButton from '../ui/BackToTopButton';
import StatsCard from '../ui/StatsCard';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import RewardsConfig from './RewardsConfig'; 
import AdminEditUserModal from './AdminEditUserModal'; 
import { FullPageSkeleton } from '../ui/PageSkeleton';
import EmptyState from '../ui/EmptyState';
import TableToolbar from '../ui/TableToolbar';
import Select from '../ui/Select';
import SegmentedControl from '../ui/SegmentedControl';
import { searchInputClassName, tableClassName, tableHeadClassName, tableHeaderCellClassName, tableBodyClassName, tableRowClassName, tableCellClassName, tableCellPrimaryClassName } from '../ui/formStyles';
import AdminEnrolaHub from '../enrola/AdminEnrolaHub';
import VendorProspectsView from '../enrola/VendorProspectsView';
import PendingGiftsView from '../enrola/PendingGiftsView';
import AdminVendorReviewsView from '../enrola/AdminVendorReviewsView';
import VideoHubAdmin from '../videos/VideoHubAdmin';
import AdminHomeView from '../enrola/AdminHomeView';
import FilterableStateDistribution from '../enrola/FilterableStateDistribution';
import ClientClubView from '../enrola/ClientClubView';
import AdminPromotionsView from '../enrola/AdminPromotionsView';
import SettingsPanel from './SettingsPanel';
import UserAvatar from '../ui/UserAvatar';
import CopyablePhone from '../ui/CopyablePhone';
import { navigateFromNotificationAction } from '../../utils/notifications';
import { useProspectConfig } from '../../hooks/useProspectConfig';
import { resolveProspectStateDistribution } from '../../utils/analytics';
import { buildAdminDashboardSnapshot } from '../../utils/analytics';
import { ELEVATE_HISPANO_SLUG } from '../../utils/elevateEvent';

function AdminDashboard() {
  const { user, logout } = useAuth();
  const { t, formatMessage } = useLanguage();
  const { unreadCount, updateCount } = useNotifications();
  const isElevateHispano = user?.company_slug === ELEVATE_HISPANO_SLUG;
  const { count: pendingGiftsBadgeCount, refreshCount: refreshPendingGiftsBadge } = usePendingGiftsMenuBadge(
    isElevateHispano ? null : 'admin'
  );
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false); 
  const [selectedUser, setSelectedUser] = useState(null);
  const [adminAnalytics, setAdminAnalytics] = useState(null);
  const [adminOverview, setAdminOverview] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importType, setImportType] = useState('vendors');
  const [importPreview, setImportPreview] = useState(null);
  const [importUploading, setImportUploading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [importSearchTerm, setImportSearchTerm] = useState('');
  const [importStatusFilter, setImportStatusFilter] = useState('all');
  
  // Users view filters
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSortBy, setUserSortBy] = useState('created_at');
  const [userSortOrder, setUserSortOrder] = useState('desc');
  const [pendingGiftsSummary, setPendingGiftsSummary] = useState(null);
  const [referralLinks, setReferralLinks] = useState([]);
  const [adminProspects, setAdminProspects] = useState([]);
  const [adminDistributionClients, setAdminDistributionClients] = useState([]);
  const [distributionLoading, setDistributionLoading] = useState(false);
  const [homeExtrasLoading, setHomeExtrasLoading] = useState(true);
  const { prospectStatuses } = useProspectConfig();

  const dashboardSnapshot = useMemo(
    () => buildAdminDashboardSnapshot(adminOverview, users),
    [adminOverview, users]
  );

  const adminProspectStateDistribution = useMemo(
    () => resolveProspectStateDistribution(
      dashboardSnapshot?.prospect_states,
      adminProspects
    ),
    [dashboardSnapshot?.prospect_states, adminProspects]
  );

  const fetchAdminAnalyticsData = useCallback(async (signal) => {
    try {
      const analyticsResponse = await fetchAdminAnalytics(null, {
        signal: asAbortSignal(signal),
      });
      setAdminAnalytics(analyticsResponse);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Error fetching admin analytics data:', error);
      toast.error(t.dashboard.failedToLoadAnalytics);
    }
  }, [t.dashboard.failedToLoadAnalytics]);

  const fetchAdminOverviewData = useCallback(async (signal) => {
    try {
      const response = await axios.get(
        '/api/admin/analytics/overview',
        withAbortSignal({ withCredentials: true }, signal),
      );
      setAdminOverview(response.data);
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('[AdminDashboard] overview fetch failed', {
        status: error.response?.status,
        body: error.response?.data,
        message: error.message,
      });
    }
  }, []);

  const fetchVendors = useCallback(async (signal) => {
    try {
      const response = await axios.get(
        '/api/admin/vendors',
        withAbortSignal({ withCredentials: true }, signal),
      );
      setVendors(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Error fetching vendors:', error);
      toast.error(t.admin.failedToLoadVendors);
    }
  }, [t.admin.failedToLoadVendors]);

  const fetchUsers = useCallback(async (signal) => {
    try {
      const response = await axios.get(
        '/api/admin/users',
        withAbortSignal({ withCredentials: true }, signal),
      );
      setUsers(response.data);
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      toast.error(t.admin.errorLoadingUsers);
    } finally {
      setLoading(false);
    }
  }, [t.admin.errorLoadingUsers]);

  useEffect(() => {
    const controller = new AbortController();
    fetchUsers(controller.signal);
    fetchAdminAnalyticsData(controller.signal);
    fetchAdminOverviewData(controller.signal);
    fetchVendors(controller.signal);
    return () => controller.abort();
  }, [fetchUsers, fetchAdminAnalyticsData, fetchAdminOverviewData, fetchVendors]);

  // Map URL path segment <-> view id (navigation)
  const pathToView = (pathname) => {
    const seg = pathname.split('/').filter(Boolean);
    const second = seg[1] || '';
    switch (second) {
      case 'users':
        return 'users';
      case 'analytics':
        return 'analytics';
      case 'settings':
        return 'settings';
      case 'import-users':
        return 'importUsers';
      case 'prospects':
        return 'prospects';
      case 'rewards':
        return 'rewards';
      case 'enrola':
        return 'enrola';
      case 'pending-gifts':
        return 'pendingGifts';
      case 'vendor-reviews':
        return 'vendorReviews';
      case 'video-hub':
        return 'videoHub';
      case 'club':
        return 'club';
      case 'promotions':
        return 'promotions';
      default:
        return 'dashboard';
    }
  };

  const viewToPath = (view) => {
    switch (view) {
      case 'users':
        return '/admin/users';
      case 'analytics':
        return '/admin/analytics';
      case 'settings':
        return '/admin/settings';
      case 'importUsers':
        return '/admin/import-users';
      case 'prospects':
        return '/admin/prospects';
      case 'rewards':
        return '/admin/rewards';
      case 'enrola':
        return '/admin/enrola';
      case 'pendingGifts':
        return '/admin/pending-gifts';
      case 'vendorReviews':
        return '/admin/vendor-reviews';
      case 'videoHub':
        return '/admin/video-hub';
      case 'club':
        return '/admin/club';
      case 'promotions':
        return '/admin/promotions';
      default:
        return '/admin';
    }
  };

  // Sync activeView from URL (pathname only — avoid fighting optimistic setActiveView before navigate)
  useEffect(() => {
    const viewFromPath = pathToView(location.pathname);
    if (location.pathname.endsWith('/notifications')) {
      setShowNotificationsModal(true);
      navigate('/admin', { replace: true });
      return;
    }
    if (isElevateHispano && viewFromPath === 'pendingGifts') {
      navigate('/admin', { replace: true });
      setActiveView('dashboard');
      return;
    }
    // Club remains Elevate Hispano exclusive.
    if (!isElevateHispano && viewFromPath === 'club') {
      navigate('/admin', { replace: true });
      setActiveView('dashboard');
      return;
    }
    // Promotions are managed only by Elevate Hispano admins.
    if (!isElevateHispano && viewFromPath === 'promotions') {
      navigate('/admin', { replace: true });
      setActiveView('dashboard');
      return;
    }
    setActiveView(viewFromPath);
  }, [location.pathname, navigate, isElevateHispano]);

  useEffect(() => {
    if (activeView !== 'dashboard') return undefined;

    let cancelled = false;

    async function fetchHomeExtras() {
      const canInviteClients = Boolean(user?.company_id);

      if (isElevateHispano) {
        if (!cancelled) {
          setPendingGiftsSummary(null);
        }
      } else {
        setHomeExtrasLoading(true);
        try {
          const giftsResponse = await axios.get('/api/admin/pending-gifts', { withCredentials: true });
          if (!cancelled) {
            setPendingGiftsSummary(giftsResponse.data);
          }
        } catch (error) {
          console.error('Error loading admin home data:', error);
        }
      }

      if (canInviteClients) {
        try {
          const linksResponse = await axios.get('/api/referral-links', { withCredentials: true });
          if (!cancelled) {
            setReferralLinks(Array.isArray(linksResponse.data) ? linksResponse.data : []);
          }
        } catch (error) {
          console.error('Error loading admin referral links:', error);
          if (!cancelled) {
            setReferralLinks([]);
          }
        }
      } else if (!cancelled) {
        setReferralLinks([]);
      }

      if (!cancelled) {
        setHomeExtrasLoading(false);
      }
    }

    fetchHomeExtras();
    fetchAdminOverviewData();
    return () => {
      cancelled = true;
    };
  }, [activeView, fetchAdminOverviewData, isElevateHispano, user?.company_id]);

  // Helper to navigate when switching views from UI controls
  const navigateToView = (view) => {
    navigate(viewToPath(view));
  };
  
  useEffect(() => {
    if (activeView !== 'analytics' && activeView !== 'dashboard') return undefined;

    let cancelled = false;

    async function loadAdminDistributionData() {
      setDistributionLoading(true);
      try {
        const [prospectsResponse, clientsResponse] = await Promise.all([
          axios.get('/api/admin/prospects', { withCredentials: true }),
          axios.get('/api/admin/clients', { withCredentials: true }),
        ]);

        if (!cancelled) {
          setAdminProspects(prospectsResponse.data || []);
          setAdminDistributionClients(clientsResponse.data || []);
        }
      } catch (error) {
        console.error('[AdminDashboard] distribution data fetch failed', {
          status: error.response?.status,
          body: error.response?.data,
          message: error.message,
        });
      } finally {
        if (!cancelled) {
          setDistributionLoading(false);
        }
      }

      if (!cancelled && activeView === 'analytics') {
        await fetchAdminOverviewData();
      }
    }

    loadAdminDistributionData();
    return () => {
      cancelled = true;
    };
  }, [activeView, fetchAdminOverviewData]);

  const downloadTemplate = async (type = 'vendors') => {
    try {
      const endpoint = type === 'vendors' 
        ? '/api/admin/vendors/import/template'
        : '/api/admin/clients/import/template';
      const filename = type === 'vendors' 
        ? 'vendors_import_template.xlsx'
        : 'clients_import_template.xlsx';
      
      const response = await axios.get(endpoint, {
        responseType: 'blob',
        withCredentials: true
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error(t.admin.failedToDownloadTemplate);
    }
  };

  const handleUploadExcel = async (file) => {
    setImportUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      if (importType === 'clients') {
        if (!selectedVendor) {
          toast.error(t.admin.pleaseSelectVendor);
          setImportUploading(false);
          return;
        }
        formData.append('vendor_id', selectedVendor);
      }
      
      const endpoint = importType === 'vendors' 
        ? '/api/admin/vendors/import/preview'
        : '/api/admin/clients/import/preview';
        
      const response = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      if (!response.data || !response.data.preview) {
        toast.error(t.admin.invalidServerResponse);
        return;
      }
      
      setImportPreview(response.data.preview);
      
      const { valid, invalid } = response.data;
      
      if (invalid > 0) {
        toast.error(formatMessage(t.admin.importPreviewErrors, { valid, invalid }), {
          duration: 4000,
        });
      } else {
        toast.success(formatMessage(t.admin.importPreviewReady, { valid }));
      }
    } catch (error) {
      toast.error(error.response?.data?.error || t.admin.failedToParseExcel);
    } finally {
      setImportUploading(false);
    }
  };

  const confirmImport = async () => {
    try {
      const endpoint = importType === 'vendors' 
        ? '/api/admin/vendors/import/confirm'
        : '/api/admin/clients/import/confirm';
        
      const response = await axios.post(endpoint, {}, { withCredentials: true });
      const userType = importType === 'vendors' ? 'vendors' : 'clients';
      const count = response.data.created || 0;
      const emailsSent = response.data.emails_sent || 0;
      toast.success(
        emailsSent > 0
          ? formatMessage(t.admin.importedCountWithEmails, { count, type: userType, emails: emailsSent })
          : formatMessage(t.admin.importedCount, { count, type: userType })
      );
      
      // Show errors if any
      if (response.data.errors && response.data.errors.length > 0) {
        toast.error(formatMessage(t.admin.importPartialErrors, { count: response.data.errors.length }), {
          duration: 4000,
        });
      }
      
      // Close modal and clear data only after successful import
      setShowImportModal(false);
      setImportPreview(null);
      setSelectedVendor('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || t.admin.importFailed);
    }
  };

  const handleNotificationClick = () => {
    setShowNotificationsModal(true); 
  };

  const handleNotificationNavigate = useCallback((actionPath) => {
    navigateFromNotificationAction(navigate, actionPath);
  }, [navigate]);

  const getPageTitle = () => {
    switch (activeView) {
      case 'analytics':
        return t.admin.systemAnalytics;
      case 'users':
        return t.admin.userManagement;
      case 'settings':
        return t.admin.systemSettings;
      case 'rewards':
        return t.admin.rewardsManagement;
      case 'prospects':
        return t.vendor?.prospects?.title || 'Prospects';
      case 'enrola':
        return t.enrola?.enrolaHub || 'Enrola';
      case 'pendingGifts':
        return t.pendingGifts.title;
      case 'vendorReviews':
        return t.vendorReviews.title;
      case 'videoHub':
        return t.videoHub.adminTitle;
      case 'club':
        return t.enrola.club;
      case 'promotions':
        return t.promotions?.adminTitle || t.promotions?.title || 'Promotions';
      default:
        return t.admin.adminDashboard;
    }
  };

  const getPageSubtitle = () => {
    switch (activeView) {
      case 'analytics':
        return t.admin.monitorSystemPerformance;
      case 'users':
        return t.admin.manageUsersDescription;
      case 'settings':
        return t.admin.configureSystemSettings;
      case 'rewards':
        return t.admin.configureRewardPoints;
      case 'prospects':
        return t.vendor?.prospects?.adminSubtitle || 'View all company prospects';
      case 'enrola':
        return t.enrola.adminHubSubtitle;
      case 'pendingGifts':
        return t.pendingGifts.adminSubtitle;
      case 'vendorReviews':
        return t.vendorReviews.subtitle;
      case 'videoHub':
        return t.videoHub.adminSubtitle;
      case 'club':
        return t.enrola?.clubView?.elevateSubtitle || t.enrola.shoppingClubBtn;
      case 'promotions':
        return t.promotions?.adminSubtitle;
      default:
        return t.enrola?.adminHome?.homeSubtitle || t.admin.welcomeBack.replace('{name}', user?.username || t.common.user);
    }
  };

  const viewUserDetails = (userData) => {
    setSelectedUser(userData);
    setShowUserModal(true);
  };
  
  // Views
  
  const adminHomeProspectStateDistribution = useMemo(
    () => resolveProspectStateDistribution(
      dashboardSnapshot?.prospect_states,
      adminProspects
    ),
    [dashboardSnapshot?.prospect_states, adminProspects]
  );

  const renderDashboard = () => (
    <AdminHomeView
      username={user?.username}
      adminOverview={dashboardSnapshot}
      adminAnalytics={adminAnalytics}
      prospectStatuses={prospectStatuses}
      prospectStateData={adminHomeProspectStateDistribution}
      clients={adminDistributionClients}
      prospects={adminProspects}
      vendors={vendors}
      distributionLoading={distributionLoading}
      pendingGifts={pendingGiftsSummary}
      pendingGiftsLoading={homeExtrasLoading}
      hidePendingGifts={isElevateHispano}
      unreadNotifications={unreadCount}
      referralLinks={referralLinks}
      showClientInvite={Boolean(user?.company_id)}
      onOpenPendingGifts={() => navigateToView('pendingGifts')}
      onOpenSettings={() => navigateToView('settings')}
      onOpenAnalytics={() => navigateToView('analytics')}
      onOpenUsers={() => navigateToView('users')}
      onOpenNotifications={handleNotificationClick}
      onOpenReferrals={() => navigateToView('enrola')}
    />
  );
  
  // Users Management View
  const renderUsersView = () => {
    // Filter and sort users
    const filteredUsers = users
      .filter(u => {
        if (userTypeFilter === 'all') return true;
        if (userTypeFilter === 'admin') return u.is_admin;
        if (userTypeFilter === 'vendor') return u.is_vendor;
        if (userTypeFilter === 'client') return u.is_client;
        return true;
      })
      .filter(u => {
        if (!userSearchTerm) return true;
        const search = userSearchTerm.toLowerCase();
        return (
          u.username?.toLowerCase().includes(search) ||
          u.email?.toLowerCase().includes(search) ||
          u.phone_numbers?.some(p => p.includes(search))
        );
      })
      .sort((a, b) => {
        let aVal = a[userSortBy];
        let bVal = b[userSortBy];
        
        if (userSortBy === 'created_at') {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        } else if (userSortBy === 'referrals_count') {
          aVal = a.referrals_count || 0;
          bVal = b.referrals_count || 0;
        }
        
        if (userSortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      });

    const userTypeStats = {
      all: users.length,
      admin: users.filter(u => u.is_admin).length,
      vendor: users.filter(u => u.is_vendor).length,
      client: users.filter(u => u.is_client).length
    };

    return (
      <div className="space-y-5 xl:space-y-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-secondary-600 ">{t.admin.totalUsers}</p>
                <p className="text-2xl font-bold text-secondary-900 ">{userTypeStats.all}</p>
              </div>
              <UsersIcon className="w-10 h-10 text-orange-500 opacity-50" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-premium p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-secondary-600 ">{t.admin.administrators}</p>
                <p className="text-2xl font-bold text-amber-600">{userTypeStats.admin}</p>
              </div>
              <ShieldCheckIcon className="w-10 h-10 text-amber-500 opacity-50" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-premium p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-secondary-600 ">{t.admin.totalVendors}</p>
                <p className="text-2xl font-bold text-red-600">{userTypeStats.vendor}</p>
              </div>
              <UserGroupIcon className="w-10 h-10 text-red-500 opacity-50" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card-premium p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-secondary-600 ">{t.admin.totalClients}</p>
                <p className="text-2xl font-bold text-success-600">{userTypeStats.client}</p>
              </div>
              <UserIcon className="w-10 h-10 text-success-500 opacity-50" />
            </div>
          </motion.div>
        </div>

        {/* Filters and Search */}
        <div className="card-premium overflow-hidden p-6 max-lg:p-4">
          <div className="flex flex-col gap-4 lg:flex-row">
            {/* Search */}
            <div className="min-w-0 flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t.admin.searchByUsername}
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className={searchInputClassName}
                />
                <EyeIcon className="w-5 h-5 text-secondary-400 absolute left-3 top-2.5" />
              </div>
            </div>

            {/* Filter by Type */}
            <div className="max-lg:mobile-scroll-x lg:flex lg:flex-wrap lg:gap-2 lg:overflow-visible lg:pb-0">
              {['all', 'admin', 'vendor', 'client'].map((type) => (
                <button
                  key={type}
                  onClick={() => setUserTypeFilter(type)}
                  className={`shrink-0 rounded-2xl px-3 py-2 text-sm font-medium transition-colors max-lg:touch-target lg:px-4 ${
                    userTypeFilter === type
                      ? 'bg-orange-500 text-white'
                      : 'bg-secondary-100 text-secondary-700   hover:bg-secondary-200 '
                  }`}
                >
                  {type === 'all' ? t.admin.all : type === 'admin' ? t.common.admin : type === 'vendor' ? t.admin.vendors : t.admin.totalClients}
                  {type !== 'all' && ` (${userTypeStats[type]})`}
                </button>
              ))}
            </div>

            {/* Sort */}
            <div className="flex flex-wrap items-center gap-2">
              <Select
                variant="compact"
                value={userSortBy}
                onChange={(e) => setUserSortBy(e.target.value)}
              >
                <option value="created_at">{t.admin.dateJoined}</option>
                <option value="username">{t.admin.username}</option>
                <option value="referrals_count">{t.dashboard.referrals}</option>
              </Select>
              <Button
                type="button"
                variant="secondary"
                size="compact"
                onClick={() => setUserSortOrder(userSortOrder === 'asc' ? 'desc' : 'asc')}
              >
                {userSortOrder === 'asc' ? '↑' : '↓'}
              </Button>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="card-premium overflow-hidden">
          <div className="hidden overflow-x-auto lg:block">
            <table className={tableClassName}>
              <thead className={tableHeadClassName}>
                <tr>
                  <th className={`${tableHeaderCellClassName} lg:px-6`}>
                    {t.admin.user}
                  </th>
                  <th className={`${tableHeaderCellClassName} lg:px-6`}>
                    {t.admin.type}
                  </th>
                  <th className={`${tableHeaderCellClassName} hidden lg:table-cell lg:px-6`}>
                    {t.admin.contact}
                  </th>
                  <th className={`${tableHeaderCellClassName} lg:px-6`}>
                    {t.dashboard.referrals}
                  </th>
                  <th className={`${tableHeaderCellClassName} hidden md:table-cell lg:px-6`}>
                    {t.admin.joined}
                  </th>
                  <th className={`${tableHeaderCellClassName} lg:px-6`}>
                    {t.admin.actions}
                  </th>
                </tr>
              </thead>
              <tbody className={tableBodyClassName}>
                {filteredUsers.map((userData, index) => (
                  <motion.tr
                    key={`${userData.user_type}-${userData.id}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className={tableRowClassName}
                  >
                    <td className={`${tableCellPrimaryClassName} whitespace-nowrap lg:px-6`}>
                      <div className="flex items-center">
                        <UserAvatar
                          src={userData.avatar_url}
                          alt={userData.username}
                          size="compact"
                          variant="brand"
                          className="flex-shrink-0 rounded-2xl"
                        />
                        <div className="ml-3 min-w-0 lg:ml-4">
                          <div className="truncate text-sm font-medium text-secondary-900 ">{userData.username}</div>
                          <div className="truncate text-sm text-secondary-500 ">{userData.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className={`${tableCellClassName} whitespace-nowrap lg:px-6`}>
                      {userData.is_admin && (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800">
                          {t.common.admin}
                        </span>
                      )}
                      {userData.is_vendor && (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          {t.admin.vendorRole}
                        </span>
                      )}
                      {userData.is_client && (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-success-100 text-success-800">
                          {t.admin.totalClients.replace('Total ', '')}
                        </span>
                      )}
                    </td>
                    <td className={`${tableCellClassName} hidden whitespace-nowrap lg:table-cell lg:px-6`}>
                      <CopyablePhone
                        value={userData.phone_numbers}
                        fallback={t.admin.noPhone}
                        className="text-sm text-secondary-900 "
                      />
                    </td>
                    <td className={`${tableCellClassName} whitespace-nowrap lg:px-6`}>
                      <div className="text-sm text-secondary-900 ">
                        {userData.referrals_count || 0}
                      </div>
                    </td>
                    <td className={`${tableCellClassName} hidden whitespace-nowrap md:table-cell lg:px-6`}>
                      {new Date(userData.created_at).toLocaleDateString()}
                    </td>
                    <td className={`${tableCellClassName} whitespace-nowrap lg:px-6`}>
                      <Button
                        type="button"
                        variant="ghost"
                        size="compact"
                        onClick={() => viewUserDetails(userData)}
                        className="text-orange-600 hover:text-orange-700 "
                      >
                        {t.admin.editUser}
                      </Button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="divide-y divide-secondary-100  lg:hidden">
            {filteredUsers.map((userData) => (
              <div key={`mobile-${userData.user_type}-${userData.id}`} className="space-y-3 p-4">
                <div className="flex items-center gap-3">
                  <UserAvatar src={userData.avatar_url} alt={userData.username} size="sm" variant="brand" />
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-secondary-900 ">{userData.username}</p>
                    <p className="truncate text-sm text-secondary-500 ">{userData.email}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 text-sm">
                  {userData.is_admin ? (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">{t.common.admin}</span>
                  ) : null}
                  {userData.is_vendor ? (
                    <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-800">{t.admin.vendorRole}</span>
                  ) : null}
                  {userData.is_client ? (
                    <span className="rounded-full bg-success-100 px-2 py-0.5 text-xs font-semibold text-success-800">
                      {t.admin.totalClients.replace('Total ', '')}
                    </span>
                  ) : null}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-secondary-500">{t.admin.contact}</p>
                    <CopyablePhone value={userData.phone_numbers} fallback={t.admin.noPhone} />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-secondary-500">{t.dashboard.referrals}</p>
                    <p className="mt-0.5 font-semibold text-secondary-900 ">{userData.referrals_count || 0}</p>
                  </div>
                </div>
                <Button type="button" variant="secondary" size="small" className="w-full" onClick={() => viewUserDetails(userData)}>
                  {t.admin.editUser}
                </Button>
              </div>
            ))}
          </div>

          {filteredUsers.length === 0 && (
            <EmptyState
              compact
              icon={UsersIcon}
              title={t.admin.noUsersFound}
              description={t.admin.tryAdjustingFilters}
              className="mx-4 mb-4"
            />
          )}
        </div>
      </div>
    );
  };

  const renderAnalyticsView = () => {
    const clientStateData = dashboardSnapshot?.client_states || {};
    const prospectStateData = adminProspectStateDistribution;
    const sd = t.common.stateDistribution;

    return (
      <div className="space-y-5 xl:space-y-6">
        {/* Overview Stats + KPI rail */}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4 xl:gap-6">
          <StatsCard
            title={t.admin.totalRevenue}
            value={`$${(dashboardSnapshot?.revenue?.total || 0).toLocaleString()}`}
            change={formatTrendData(adminAnalytics, 'revenue')?.change || 0}
            changeType={formatTrendData(adminAnalytics, 'revenue')?.changeType || "neutral"}
            icon={CurrencyDollarIcon}
            gradient="from-green-500 to-emerald-500"
            description={t.admin.platformRevenue}
            trend={formatTrendData(adminAnalytics, 'revenue')?.data || [0, 0, 0, 0, 0, 0, 0]}
            periodText="vs last period"
            trendPeriodText={t.statsCard.last7Days}
          />
          
          <StatsCard
            title={t.admin.totalUsers}
            value={dashboardSnapshot?.users?.total || 0}
            change={formatTrendData(adminAnalytics, 'users')?.change || 0}
            changeType={formatTrendData(adminAnalytics, 'users')?.changeType || "neutral"}
            icon={UsersIcon}
            gradient="from-orange-500 to-amber-500"
            description={t.admin.allUsers}
            trend={formatTrendData(adminAnalytics, 'users')?.data || [0, 0, 0, 0, 0, 0, 0]}
            periodText="vs last period"
            trendPeriodText={t.statsCard.last7Days}
            animated
          />
          
          <StatsCard
            title={t.admin.totalReferrals}
            value={dashboardSnapshot?.referrals?.total || 0}
            change={formatTrendData(adminAnalytics, 'referrals')?.change || 0}
            changeType={formatTrendData(adminAnalytics, 'referrals')?.changeType || "neutral"}
            icon={LinkIcon}
            gradient="from-red-500 to-pink-500"
            description={t.admin.platformReferrals}
            trend={formatTrendData(adminAnalytics, 'referrals')?.data || [0, 0, 0, 0, 0, 0, 0]}
            periodText="vs last period"
            trendPeriodText={t.statsCard.last7Days}
            animated
          />
          

          {/* Key Performance Indicators — rail column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-premium p-4 xl:p-5"
          >
            <h3 className="mb-3 flex items-center text-base font-semibold tracking-tight text-secondary-900  xl:mb-4 xl:text-lg">
              <TrophyIcon className="mr-2 h-5 w-5 text-success-500" />
              {t.admin.keyPerformanceIndicators}
            </h3>

            <div className="grid gap-3 sm:max-xl:grid-cols-2">
              <div className="flex items-center justify-between rounded-2xl border border-secondary-200/80 border-l-4 border-l-orange-500 bg-white p-3.5  ">
                <div className="min-w-0">
                  <p className="mb-1 truncate text-xs text-secondary-600  sm:text-sm">{t.admin.activeReferralLinks}</p>
                  <p className="text-xl font-bold text-orange-600 sm:text-2xl">{dashboardSnapshot?.links?.active || 0}</p>
                </div>
                <LinkIcon className="h-8 w-8 shrink-0 text-orange-500 opacity-50" />
              </div>

              <div className="flex items-center justify-between rounded-2xl border border-secondary-200/80 border-l-4 border-l-red-500 bg-white p-3.5  ">
                <div className="min-w-0">
                  <p className="mb-1 truncate text-xs text-secondary-600  sm:text-sm">{t.admin.totalClicks}</p>
                  <p className="text-xl font-bold text-red-600 sm:text-2xl">{dashboardSnapshot?.links?.total_clicks || 0}</p>
                </div>
                <EyeIcon className="h-8 w-8 shrink-0 text-red-500 opacity-50" />
              </div>

              <div className="flex items-center justify-between rounded-2xl border border-secondary-200/80 border-l-4 border-l-success-500 bg-white p-3.5  ">
                <div className="min-w-0">
                  <p className="mb-1 truncate text-xs text-secondary-600  sm:text-sm">Active Referrers</p>
                  <p className="text-xl font-bold text-success-600 sm:text-2xl">{dashboardSnapshot?.referrals?.active_referrers || 0}</p>
                </div>
                <UserGroupIcon className="h-8 w-8 shrink-0 text-success-500 opacity-50" />
              </div>

              <div className="flex items-center justify-between rounded-2xl border border-secondary-200/80 border-l-4 border-l-amber-500 bg-white p-3.5  ">
                <div className="min-w-0">
                  <p className="mb-1 truncate text-xs text-secondary-600  sm:text-sm">{t.admin.avgReferralsPerClient}</p>
                  <p className="text-xl font-bold text-amber-600 sm:text-2xl">
                    {dashboardSnapshot?.averages?.referrals_per_client?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <ChartBarIcon className="h-8 w-8 shrink-0 text-amber-500 opacity-50" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* State distributions — side by side on very wide screens */}
        <div className="grid gap-5 min-[1600px]:grid-cols-2">
          <FilterableStateDistribution
            variant="client"
            title={t.admin.clientStateDistribution}
            clients={adminDistributionClients}
            fallbackStateData={clientStateData}
            labels={{
              affiliated: t.admin.affiliated,
              contacted: t.admin.contacted,
              visited: t.admin.visited,
              completed: t.admin.completed,
            }}
            vendors={vendors}
            showVendorFilter
            filterLabels={sd}
            loading={distributionLoading}
          />

          <FilterableStateDistribution
            variant="prospect"
            title={t.admin.prospectStateDistribution}
            prospects={adminProspects}
            fallbackStateData={prospectStateData}
            prospectStatuses={prospectStatuses}
            t={t}
            vendors={vendors}
            showVendorFilter
            filterLabels={sd}
            delay={0.05}
            loading={distributionLoading}
          />
        </div>

        {/* Top Performers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 xl:gap-6">
          {/* Top Vendors */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-premium p-5 xl:p-6"
          >
            <h3 className="text-lg font-semibold text-secondary-900  mb-4 flex items-center">
              <TrophyIcon className="w-5 h-5 text-amber-500 mr-2" />
              {t.admin.topVendors}
            </h3>
            
            <div className="space-y-3">
              {dashboardSnapshot?.top_performers?.vendors?.length > 0 ? (
                dashboardSnapshot.top_performers.vendors.map((vendor, index) => (
                  <div
                    key={vendor.id}
                    className="flex items-center justify-between rounded-2xl border border-secondary-200/60 bg-secondary-50/80 p-3 transition-colors hover:bg-secondary-100   "
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                        index === 0 ? 'bg-amber-500' :
                        index === 1 ? 'bg-secondary-400' :
                        index === 2 ? 'bg-orange-600' :
                        'bg-secondary-300'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-secondary-900 ">{vendor.username}</p>
                        <p className="text-xs text-secondary-500 ">{vendor.clients_count} {t.admin.clientsLabel}</p>
                      </div>
                    </div>
                    <div className="text-right">
                        <p className="font-semibold text-success-600">{vendor.referrals_count} {t.dashboard.referrals}</p>
                      <p className="text-xs text-secondary-500 ">${vendor.commission}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-secondary-500  py-4">{t.admin.noVendorsYet}</p>
              )}
            </div>
          </motion.div>

          {/* Top Clients */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card-premium p-5 xl:p-6"
          >
            <h3 className="text-lg font-semibold text-secondary-900  mb-4 flex items-center">
              <TrophyIcon className="w-5 h-5 text-success-500 mr-2" />
              {t.admin.topClients}
            </h3>
            
            <div className="space-y-3">
              {dashboardSnapshot?.top_performers?.clients?.length > 0 ? (
                dashboardSnapshot.top_performers.clients.map((client, index) => (
                  <div
                    key={client.id}
                    className="flex items-center justify-between rounded-2xl border border-secondary-200/60 bg-secondary-50/80 p-3 transition-colors hover:bg-secondary-100   "
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                        index === 0 ? 'bg-amber-500' :
                        index === 1 ? 'bg-secondary-400' :
                        index === 2 ? 'bg-orange-600' :
                        'bg-secondary-300'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-secondary-900 ">{client.username}</p>
                        <p className="text-xs text-secondary-500 ">{client.reward_points} points</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-orange-600">{client.referrals_count} referrals</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-secondary-500  py-4">{t.admin.noActiveClientsYet}</p>
              )}
            </div>
          </motion.div>
        </div>

        {/* Revenue Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card-premium p-5 xl:p-6"
        >
          <h3 className="text-lg font-semibold text-secondary-900  mb-6 flex items-center">
            <CurrencyDollarIcon className="w-5 h-5 text-success-500 mr-2" />
            {t.admin.revenueAnalytics}
          </h3>
          
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3 xl:gap-6">
            <div className="rounded-2xl border border-secondary-200/80 border-l-4 border-l-success-500 bg-white p-4  ">
              <p className="text-sm text-secondary-600  mb-2">{t.admin.totalPlatformRevenue}</p>
              <p className="text-3xl font-bold text-success-600">
                ${(dashboardSnapshot?.revenue?.total || 0).toLocaleString()}
              </p>
              <p className="text-xs text-secondary-500  mt-2">
                {t.admin.fromReferrals.replace('{count}', dashboardSnapshot?.referrals?.total || 0)}
              </p>
            </div>

            <div className="rounded-2xl border border-secondary-200/80 border-l-4 border-l-orange-500 bg-white p-4  ">
              <p className="text-sm text-secondary-600  mb-2">{t.admin.revenuePerReferral}</p>
              <p className="text-3xl font-bold text-orange-600">
                ${dashboardSnapshot?.revenue?.per_referral || 25}
              </p>
              <p className="text-xs text-secondary-500  mt-2">{t.admin.standardCommission}</p>
            </div>

            <div className="rounded-2xl border border-secondary-200/80 border-l-4 border-l-red-500 bg-white p-4  ">
              <p className="text-sm text-secondary-600  mb-2">{t.admin.revenuePerVendor}</p>
              <p className="text-3xl font-bold text-red-600">
                ${(dashboardSnapshot?.revenue?.per_vendor || 0).toFixed(2)}
              </p>
              <p className="text-xs text-secondary-500  mt-2">
                {t.admin.averageAcrossVendors.replace('{count}', dashboardSnapshot?.users?.vendors || 0)}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  const renderImportUsersView = () => {
    const tb = t.common.table;
    const normalizedImportSearch = importSearchTerm.trim().toLowerCase();
    const filteredImportPreview = (importPreview || []).filter((row) => {
      if (importStatusFilter === 'ready' && row.status !== 'ready') return false;
      if (importStatusFilter === 'errors' && row.status === 'ready') return false;
      if (!normalizedImportSearch) return true;
      const haystack = [
        String(row.row),
        row.username,
        row.email,
        row.phone,
        row.referred_by,
        row.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedImportSearch);
    });
    const hasImportFilters =
      importSearchTerm.trim() !== '' || importStatusFilter !== 'all';
    const clearImportFilters = () => {
      setImportSearchTerm('');
      setImportStatusFilter('all');
    };

    return (
    <div className="space-y-6 xl:space-y-7">
      {/* Import Type Selection */}
      <div className="card-premium p-6 max-lg:p-4">
        <div className="mb-4 flex flex-col gap-2 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-xl font-semibold text-secondary-900 ">{t.admin.importUsersFromExcel}</h3>
            <p className="text-sm text-secondary-600 ">{t.admin.chooseImportType}</p>
          </div>
        </div>
        
        {/* Import Type Tabs */}
        <div className="mb-4 w-full lg:mb-6">
          <SegmentedControl
            value={importType}
            onChange={(type) => {
              if (type === importType) return;
              setImportType(type);
              setImportPreview(null);
              if (type === 'clients') {
                fetchVendors();
              } else {
                setSelectedVendor('');
              }
            }}
            options={[
              { value: 'vendors', label: t.admin.importVendors },
              { value: 'clients', label: t.admin.importClients },
            ]}
            layoutId="admin-import-type-segment"
            aria-label={t.admin.importUsersFromExcel}
            className="w-full max-lg:flex max-lg:[&_.ios-segment]:flex-1 lg:w-auto"
          />
        </div>
        {/* Simplified Upload for better UX */}
        <div className="space-y-4">
          {importType === 'clients' && (
            <div className="bg-orange-50 p-4 rounded-xl">
              <label className="block text-sm font-medium text-secondary-700  mb-2">
                {t.admin.selectVendor}
              </label>
              <Select
                value={selectedVendor}
                onChange={(e) => setSelectedVendor(e.target.value)}
                onFocus={() => {
                  void fetchVendors();
                }}
                className="input-field"
                disabled={importPreview !== null}
              >
                <option value="">{t.admin.selectAVendor}</option>
                {[...vendors].sort((a, b) => a.username.localeCompare(b.username)).map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.username} ({vendor.email})
                  </option>
                ))}
              </Select>
              <p className="text-xs text-secondary-500  mt-1">
                {t.admin.allImportedClientsAssigned}
              </p>
            </div>
          )}
          
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-3 [&_button]:w-full sm:[&_button]:w-auto">
              <Button variant="secondary" onClick={() => downloadTemplate(importType)} icon={UserPlusIcon}>
                {t.admin.downloadTemplate}
              </Button>
              <Button 
                variant="primary" 
                onClick={() => setShowImportModal(true)} 
                icon={UserPlusIcon}
              >
                {t.admin.uploadFileToPreview}
              </Button>
          </div>
        </div>
      </div>
      
      {/* Import Preview */}
      {importPreview && importPreview.length > 0 && (
        <div className="card-premium p-5 xl:p-6">
          <h4 className="text-base sm:text-lg font-semibold text-secondary-900  mb-4">
            {formatMessage(t.admin.importPreviewTitle, {
              ready: importPreview.filter(r => r.status === 'ready').length,
              errors: importPreview.filter(r => r.status !== 'ready').length,
            })}
          </h4>
          <p className="text-xs sm:text-sm text-secondary-600  mb-4">
            {t.admin.importPreviewHint}
          </p>
          <TableToolbar
            className="mb-4"
            searchValue={importSearchTerm}
            onSearchChange={setImportSearchTerm}
            searchPlaceholder={t.admin.searchImportPreview}
            filters={[
              {
                id: 'importStatus',
                label: t.admin.filterImportStatus,
                value: importStatusFilter,
                onChange: setImportStatusFilter,
                options: [
                  { value: 'all', label: tb.filterAll },
                  { value: 'ready', label: tb.filterReady },
                  { value: 'errors', label: tb.filterErrors },
                ],
              },
            ]}
            hasActiveFilters={hasImportFilters}
            onClearFilters={clearImportFilters}
            resultCount={filteredImportPreview.length}
            totalCount={importPreview.length}
          />
          <div className="hidden max-h-96 overflow-x-auto overflow-y-auto rounded-2xl border border-secondary-200  lg:block">
            <table className={tableClassName}>
              <thead className={`${tableHeadClassName} sticky top-0`}>
                <tr>
                  <th className={tableHeaderCellClassName}>{t.admin.importColRow}</th>
                  <th className={tableHeaderCellClassName}>{t.admin.username}</th>
                  <th className={tableHeaderCellClassName}>{t.common.email}</th>
                  <th className={tableHeaderCellClassName}>{t.common.phone}</th>
                  {importType === 'clients' && (
                    <th className={tableHeaderCellClassName}>{t.admin.importColReferredBy}</th>
                  )}
                  <th className={tableHeaderCellClassName}>{t.common.status}</th>
                </tr>
              </thead>
              <tbody className={tableBodyClassName}>
                {filteredImportPreview.map((row, index) => (
                  <tr key={`import-${row.row}-${index}`} className={`${tableRowClassName} ${row.status !== 'ready' ? 'bg-error-50/60 ' : ''}`}>
                    <td className={`${tableCellClassName} whitespace-nowrap`}>{row.row}</td>
                    <td className={`${tableCellPrimaryClassName} whitespace-nowrap`}>{row.username}</td>
                    <td className={`${tableCellClassName} whitespace-nowrap`}>{row.email}</td>
                    <td className={`${tableCellClassName} whitespace-nowrap`}>
                      <CopyablePhone value={row.phone} />
                    </td>
                    {importType === 'clients' && (
                      <td className={`${tableCellClassName} whitespace-nowrap`}>{row.referred_by || '-'}</td>
                    )}
                    <td className={`${tableCellClassName} whitespace-nowrap`}>
                      {row.status === 'ready' ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-success-100 text-success-700">{t.admin.importStatusReady}</span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-error-100 text-error-700">{row.status}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredImportPreview.length === 0 ? (
              <EmptyState
                compact
                title={tb.noMatchingRows}
                description={tb.clearFilters}
                className="p-6"
              />
            ) : null}
          </div>

          <div className="divide-y divide-secondary-100  lg:hidden">
            {filteredImportPreview.map((row, index) => (
              <div
                key={`import-mobile-${row.row}-${index}`}
                className={`space-y-2 p-4 ${row.status !== 'ready' ? 'bg-error-50/50 ' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
                    {t.admin.importColRow} {row.row}
                  </span>
                  {row.status === 'ready' ? (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-success-100 text-success-700">{t.admin.importStatusReady}</span>
                  ) : (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-error-100 text-error-700">{row.status}</span>
                  )}
                </div>
                <p className="text-sm font-semibold text-secondary-900 ">{row.username}</p>
                <p className="text-sm text-secondary-600 ">{row.email}</p>
                <CopyablePhone value={row.phone} className="text-sm" />
                {importType === 'clients' ? (
                  <p className="text-xs text-secondary-500 ">
                    {t.admin.importColReferredBy}: {row.referred_by || '-'}
                  </p>
                ) : null}
              </div>
            ))}
            {filteredImportPreview.length === 0 ? (
              <EmptyState compact title={tb.noMatchingRows} description={tb.clearFilters} className="p-6" />
            ) : null}
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <Button variant="secondary" onClick={() => setImportPreview(null)}>
              {t.admin.cancelPreview}
            </Button>
            <Button 
              variant="primary" 
              onClick={confirmImport}
              disabled={importPreview.filter(r => r.status === 'ready').length === 0}
            >
              {formatMessage(t.admin.importRecords, { count: importPreview.filter(r => r.status === 'ready').length })}
            </Button>
          </div>
        </div>
      )}
    </div>
    );
  };
  
  const renderContent = () => {
    switch (activeView) {
      case 'users':
        return renderUsersView();
      case 'analytics':
        return renderAnalyticsView();
      case 'settings':
        return <SettingsPanel />;
      case 'importUsers':
        return renderImportUsersView();
      case 'rewards':
        return <RewardsConfig />;
      case 'prospects':
        return <VendorProspectsView mode="admin" />;
      case 'enrola':
        return <AdminEnrolaHub />;
      case 'pendingGifts':
        return isElevateHispano
          ? renderDashboard()
          : <PendingGiftsView role="admin" onPendingCountChange={refreshPendingGiftsBadge} />;
      case 'vendorReviews':
        return <AdminVendorReviewsView />;
      case 'videoHub':
        return <VideoHubAdmin />;
      case 'club':
        return <ClientClubView />;
      case 'promotions':
        return <AdminPromotionsView />;
      default:
        return renderDashboard();
    }
  };

  // Pantalla de carga de old.py
  if (loading) {
    return <FullPageSkeleton />;
  }

  const ah = t.enrola?.adminHome || {};
  const adminHubStats = [
    {
      id: 'users',
      label: ah.statUsers || 'Users',
      value: dashboardSnapshot?.users?.total ?? 0,
    },
    {
      id: 'referrals',
      label: ah.statReferrals || 'Referrals',
      value: dashboardSnapshot?.referrals?.total ?? 0,
      highlight: true,
    },
  ];

  return (
    <DashboardLayout
      activeView={activeView}
      sidebarCollapsed={sidebarCollapsed}
      setSidebarCollapsed={setSidebarCollapsed}
      sidebarProps={{
        activeView,
        setActiveView: navigateToView,
        onLogout: logout,
        notificationsCount: unreadCount,
        menuBadges: { pendingGifts: isElevateHispano ? 0 : pendingGiftsBadgeCount },
      }}
      topBarProps={{
        title: getPageTitle(),
        subtitle: getPageSubtitle(),
        onNotificationClick: handleNotificationClick,
        notificationsCount: unreadCount,
        searchValue,
        onSearchChange: setSearchValue,
        onSettingsClick: () => navigateToView('settings'),
        hubStats: adminHubStats,
        pointsBalance: Number(user?.reward_points) || 0,
      }}
      overlays={
        <>
      <AdminEditUserModal
        isOpen={showUserModal}
        onClose={() => {
          setShowUserModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        t={t}
        formatMessage={formatMessage}
        onSaved={(updatedUser) => {
          if (!updatedUser?.id) {
            fetchUsers();
            return;
          }
          setSelectedUser(updatedUser);
          setUsers((prev) => prev.map((row) => (
            row.id === updatedUser.id && row.user_type === updatedUser.user_type
              ? { ...row, ...updatedUser }
              : row
          )));
          fetchUsers();
        }}
      />

      {/* Import Modal */}
      <Modal 
        isOpen={showImportModal} 
        onClose={() => {
          setShowImportModal(false);
        }} 
        title={formatMessage(t.admin.importModalTitle, {
          type: importType === 'vendors'
            ? t.admin.totalVendors.replace(/^Total /i, '')
            : t.admin.totalClients.replace(/^Total /i, ''),
        })}
        size="large"
      >
        <div className="space-y-4">
          <div className="text-sm text-secondary-600 ">
            {t.admin.importModalInstructions}
            {importType === 'clients' && !selectedVendor && (
              <div className="mt-2 rounded-2xl bg-error-50 p-2 text-error-700">
                {t.admin.selectVendorBeforeUpload}
              </div>
            )}
          </div>
          <div>
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  handleUploadExcel(e.target.files[0]);
                }
              }}
              className="block w-full text-sm text-secondary-700  file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100   "
              disabled={importUploading || (importType === 'clients' && !selectedVendor)}
            />
          </div>
          {importUploading && (
            <div className="text-sm text-secondary-600 ">{t.admin.uploadingParsing}</div>
          )}
        </div>
      </Modal>

      {/* Notifications modal */}
      <NotificationsCenter
        isOpen={showNotificationsModal}
        onClose={() => setShowNotificationsModal(false)}
        onNotificationUpdate={updateCount}
        onNavigate={handleNotificationNavigate}
      />
      <BackToTopButton />
        </>
      }
    >
      {renderContent()}
    </DashboardLayout>
  );
}

export default AdminDashboard;